// Shared visuals for the social layer: tokens (light + dark glass), avatar, badges.
import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { makeTokens, SYSTEM_STACK } from '../journalDesign';
import { useDarkMode } from '../../hooks/useDarkMode';

export const FONT = SYSTEM_STACK;

export function useSocialT() {
  const { darkMode } = useDarkMode();
  return useMemo(() => {
    const T = makeTokens(darkMode);
    return {
      ...T, dark: darkMode,
      accent: darkMode ? '#0A84FF' : '#0071E3',
      glass: darkMode ? 'rgba(38,38,41,0.78)' : 'rgba(255,255,255,0.80)',
      glassBorder: darkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
      glassShadow: darkMode
        ? '0 18px 50px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.4)'
        : '0 18px 50px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.06)',
      hover: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
      chipBg: darkMode ? 'rgba(10,132,255,0.16)' : 'rgba(0,113,227,0.09)',
      chipHover: darkMode ? 'rgba(10,132,255,0.28)' : 'rgba(0,113,227,0.17)',
    };
  }, [darkMode]);
}

export const glassSx = (T) => ({
  bgcolor: T.glass,
  backdropFilter: 'blur(28px) saturate(180%)',
  WebkitBackdropFilter: 'blur(28px) saturate(180%)',
  border: `1px solid ${T.glassBorder}`,
  boxShadow: T.glassShadow,
  fontFamily: FONT,
  color: T.ink,
});

export const popIn = {
  '@keyframes socPop': { from: { opacity: 0, transform: 'translateY(4px) scale(0.985)' }, to: { opacity: 1, transform: 'none' } },
  animation: 'socPop 140ms cubic-bezier(.2,.8,.2,1)',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

const hash = (s) => { let h = 0; for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; };
export const gradientFor = (seed) => {
  const h = hash(String(seed || '?')) % 360;
  return `linear-gradient(135deg, hsl(${h} 62% 58%), hsl(${(h + 48) % 360} 66% 44%))`;
};

export function Avatar({ user, size = 32, ring }) {
  const [broken, setBroken] = useState(false);
  const name = (user && (user.display_name || user.handle)) || '?';
  const src = user && user.avatar_url && !broken ? user.avatar_url : null;
  return (
    <Box aria-hidden sx={{
      width: size, height: size, flex: `0 0 ${size}px`, borderRadius: '50%', overflow: 'hidden', position: 'relative',
      background: gradientFor(user && (user.handle || user.user_id)), color: '#fff', display: 'grid', placeItems: 'center',
      fontWeight: 600, fontSize: Math.round(size * 0.4), fontFamily: FONT,
      boxShadow: ring || 'none',
    }}>
      {src
        ? <Box component="img" src={src} alt="" loading="lazy" onError={() => setBroken(true)} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : name.trim().charAt(0).toUpperCase()}
    </Box>
  );
}

const BADGES = {
  proven: { label: 'Proven', path: 'M12 2l2.4 2.1 3.2-.2 1 3 2.7 1.8-1 3 1 3-2.7 1.8-1 3-3.2-.2L12 22l-2.4-2.1-3.2.2-1-3L2.7 15.3l1-3-1-3L5.4 7.5l1-3 3.2.2L12 2zm-1.2 13.2l5-5-1.1-1.1-3.9 3.9-1.7-1.7-1.1 1.1 2.8 2.8z', tone: 'green' },
  staff: { label: 'Staff', path: 'M12 2l8 3v6c0 5-3.4 9.3-8 11-4.6-1.7-8-6-8-11V5l8-3z', tone: 'accent' },
  founder: { label: 'Founder', path: 'M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.1 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8L12 2z', tone: 'gold' },
};

export function BadgeIcons({ badges, size = 14, labels = false }) {
  const T = useSocialT();
  const list = (badges || []).filter((b) => BADGES[b]);
  if (!list.length) return null;
  const tone = (t) => (t === 'green' ? T.green : t === 'gold' ? (T.dark ? '#FFD60A' : '#B25000') : T.accent);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, verticalAlign: 'middle' }}>
      {list.map((b) => (
        <Box component="span" key={b} title={BADGES[b].label} aria-label={BADGES[b].label} role="img"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: tone(BADGES[b].tone),
            ...(labels ? { px: 0.9, py: 0.2, borderRadius: 999, fontSize: 11, fontWeight: 600, bgcolor: T.hover } : {}) }}>
          <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={BADGES[b].path} fillRule="evenodd" /></svg>
          {labels && BADGES[b].label}
        </Box>
      ))}
    </Box>
  );
}

export function pill(T, { primary = false, busy = false } = {}) {
  return {
    flex: 1, minHeight: 34, px: 1.5, borderRadius: 999, border: 0, cursor: busy ? 'default' : 'pointer', fontFamily: FONT,
    fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', opacity: busy ? 0.6 : 1,
    bgcolor: primary ? T.btnPriBg : T.btnSecBg, color: primary ? T.btnPriText : T.btnSecText,
    transition: 'background-color 120ms, transform 120ms',
    '&:hover': { bgcolor: primary ? T.btnPriHover : T.btnSecHover },
    '&:active': { transform: 'scale(0.97)' },
    '&:focus-visible': { outline: `2px solid ${T.accent}`, outlineOffset: 2 },
  };
}
